import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '../../i18n/uiText';
import { jsonHeadersWithAuth } from '../../utils/webAuth';
import { requestAuthPopup } from '../../utils/authPopup';
import { telegramService } from '../../services/telegram';
import { resolveAiLimitMessage } from '../../utils/aiLimitMessage';

export default function AIInterpretation({ bookId, chapter, verse, onClose }) {
  const navigate = useNavigate();
  const [interpretation, setInterpretation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('meaning');
  const [toast, setToast] = useState(null);
  const tabsContainerRef = useRef(null);
  const tabButtonRefs = useRef({});
  const loadingRef = useRef(false);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    // Блокируем скролл body при открытии модального окна
    document.body.style.overflow = 'hidden';
    
    return () => {
      // Разблокируем скролл при закрытии
      document.body.style.overflow = '';
      // Отменяем активный запрос при размонтировании
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    loadInterpretation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId, chapter, verse]);

  // При появлении толкования прокручиваем карусель табов в начало
  useEffect(() => {
    if (interpretation && tabsContainerRef.current) {
      tabsContainerRef.current.scrollLeft = 0;
    }
  }, [interpretation]);

  const loadInterpretation = async () => {
    // Если уже идет загрузка, не запускаем новый запрос
    if (loadingRef.current) {
      return;
    }

    // Отменяем предыдущий запрос, если он еще активен
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Создаем новый AbortController для этого запроса
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    loadingRef.current = true;
    setLoading(true);
    setError(null);
    
    try {
      // Получаем язык пользователя из localStorage
      const userLanguage = localStorage.getItem('bibleLanguage') || 'en';
      const API_BASE = import.meta.env.VITE_API_URL || 'https://verbum-api-production.up.railway.app/api';
      const response = await fetch(`${API_BASE}/interpret`, {
        method: 'POST',
        headers: jsonHeadersWithAuth({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          bookId: parseInt(bookId),
          chapter: parseInt(chapter),
          verse: parseInt(verse),
          language: userLanguage,
        }),
        signal: abortController.signal,
      });

      if (response.status === 401) {
        const next = `${window.location.pathname}${window.location.search}`;
        requestAuthPopup(next, true);
        throw new Error('__AUTH_REQUIRED__');
      }
      if (response.status === 429) {
        const payload = await response.json().catch(() => null);
        if (payload?.code === 'DAILY_AI_LIMIT_REACHED') {
          throw new Error(resolveAiLimitMessage(payload, t, 'interpretation'));
        }
      }
      if (!response.ok) {
        throw new Error(t('error'));
      }
      
      const data = await response.json();
      setInterpretation(data);
    } catch (err) {
      // Игнорируем ошибку, если запрос был отменен
      if (err.name === 'AbortError') {
        return;
      }
      if (err?.message === '__AUTH_REQUIRED__') return;
      setError(err.message);
    } finally {
      loadingRef.current = false;
      setLoading(false);
      // Очищаем ссылку на AbortController, если это был текущий запрос
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  };

  const tabs = [
    { id: 'meaning', label: t('interpretation'), icon: '📖', title: t('interpretation') },
    { id: 'faq', label: t('faqTab'), icon: '❓', title: t('faqTabTitle') },
    { id: 'original', label: t('originalTab'), icon: '📝', title: t('originalTab') },
    { id: 'apply', label: t('today'), icon: '🧭', title: t('today') },
    { id: 'context', label: t('contextTab'), icon: '📜', title: t('bookContext') },
    { id: 'theological', label: t('theology'), icon: '✝️', title: t('theology') },
    { id: 'culture', label: t('culture'), icon: '🏺', title: `${t('culture')} / ${t('historical')}` },
  ];

  const getActiveTabTitle = () => {
    const tab = tabs.find(t => t.id === activeTab);
    return tab ? `${tab.icon} ${tab.title}` : '';
  };

  const handleCopyLink = async () => {
    const isTelegram = telegramService.isTelegramWebApp;
    let link = '';

    if (isTelegram) {
      // Создаем ссылку для Telegram бота с startapp
      // Используем имя бота из конфигурации или по умолчанию
      const botUsername = 'VerbumBibleBot'; // Имя бота из промпта
      const startParam = `${bookId}_${chapter}_${verse}_interpretation`;
      link = `https://t.me/${botUsername}/app?startapp=${startParam}`;
    } else {
      // Создаем обычную веб-ссылку
      link = `${window.location.origin}/synodal/${bookId}/${chapter}/${verse}/interpretation`;
    }

    // В Telegram Mini App используем специальный подход
    if (isTelegram) {
      try {
        // Пробуем использовать Clipboard API
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(link);
          setToast(t('linkCopied'));
          setTimeout(() => setToast(null), 2000);
        } else {
          // Если Clipboard API недоступен, показываем ссылку для ручного копирования
          telegramService.showAlert(`Link:\n${link}\n\n${t('copyManually')}`);
        }
      } catch (error) {
        console.error('Ошибка при копировании:', error);
        // Fallback: показываем ссылку для ручного копирования
        telegramService.showAlert(`Link:\n${link}\n\n${t('copyManually')}`);
      }
    } else {
      // В обычном браузере
      try {
        await navigator.clipboard.writeText(link);
        setToast(t('linkCopied'));
        setTimeout(() => setToast(null), 2000);
      } catch (error) {
        console.error('Ошибка при копировании:', error);
        // Fallback для старых браузеров
        const textArea = document.createElement('textarea');
        textArea.value = link;
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          setToast(t('linkCopied'));
          setTimeout(() => setToast(null), 2000);
        } catch (err) {
          alert(`Link:\n${link}\n\n${t('copyManually')}`);
        }
        document.body.removeChild(textArea);
      }
    }
  };

  // Рендер ссылки на место писания
  const renderCrossRef = (ref, idx) => {
    // Если ref — объект с bookId, chapter, verse
    if (typeof ref === 'object' && ref.bookId) {
      const url = `/synodal/${ref.bookId}/${ref.chapter}${ref.verse ? `/${ref.verse}` : ''}`;
      return (
        <button
          key={idx} 
          onClick={() => {
            onClose();
            navigate(url);
          }}
          className="ai-cross-ref ai-cross-ref-link"
        >
          {ref.text}
        </button>
      );
    }
    // Если ref — строка (старый формат)
    return <span key={idx} className="ai-cross-ref">{ref}</span>;
  };

  const renderTabContent = () => {
    if (!interpretation) return null;

    switch (activeTab) {
      case 'meaning':
        return (
          <div className="ai-tab-content">
            {interpretation.meaning && (
              <>
                <p className="ai-summary">{interpretation.meaning.summary}</p>
                {interpretation.definitions && interpretation.definitions.length > 0 && (
                  <div className="ai-definitions">
                    <span className="ai-section-label">📚 {t('terms')}</span>
                    <div className="ai-definitions-list">
                      {interpretation.definitions.map((def, idx) => (
                        <div key={idx} className="ai-definition-item">
                          <span className="ai-definition-term">{def.term}</span>
                          <span className="ai-definition-text">{def.definition}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );

      case 'theological':
        return (
          <div className="ai-tab-content">
            {interpretation.theological ? (
              <>
                {interpretation.theological.themes && interpretation.theological.themes.length > 0 && (
                  <div className="ai-theology-section">
                    <span className="ai-section-label">📚 {t('theology')}</span>
                    <div className="ai-themes">
                      {interpretation.theological.themes.map((theme, idx) => (
                        <span key={idx} className="ai-theme-tag">{theme}</span>
                      ))}
                    </div>
                  </div>
                )}
                {interpretation.theological.christConnection && (
                  <div className="ai-theology-section">
                    <span className="ai-section-label">✝️ {t('christConnection')}</span>
                    <p>{interpretation.theological.christConnection}</p>
                  </div>
                )}
                {interpretation.theological.typology && (
                  <div className="ai-theology-section">
                    <span className="ai-section-label">🔗 {t('typology')}</span>
                    <p>{interpretation.theological.typology}</p>
                  </div>
                )}
                {interpretation.crossReferences && interpretation.crossReferences.length > 0 && (
                  <div className="ai-theology-section">
                    <span className="ai-section-label">📖 {t('crossReferences')}</span>
                    <div className="ai-cross-refs">
                      {interpretation.crossReferences.map((ref, idx) => renderCrossRef(ref, idx))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="ai-no-content">{t('noData')}</p>
            )}
          </div>
        );

      case 'faq': {
        const faqList = Array.isArray(interpretation.faq)
          ? interpretation.faq
          : (Array.isArray(interpretation.FAQ) ? interpretation.FAQ : []);

        return (
          <div className="ai-tab-content">
            {faqList.length > 0 ? (
              <div className="ai-faq-list">
                <span className="ai-section-label">❓ {t('faqByText')}</span>
                <div className="ai-definitions-list">
                  {faqList.map((item, idx) => {
                    const question = item?.question ?? item?.Question ?? '';
                    const answer = item?.answer ?? item?.Answer ?? '';
                    return (
                      <div key={idx} className="ai-definition-item">
                        {question && <span className="ai-definition-term">{question}</span>}
                        {answer && <span className="ai-definition-text">{answer}</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p className="ai-no-content">{t('noFaq')}</p>
            )}
          </div>
        );
      }

      case 'context':
        return (
          <div className="ai-tab-content">
            {interpretation.context && (
              <>
                {interpretation.context.immediate && (
                  <div className="ai-context-section">
                    <span className="ai-section-label">📍 {t('immediateContext')}</span>
                    <p>{interpretation.context.immediate}</p>
                  </div>
                )}
                {interpretation.context.bookContext && (
                  <div className="ai-context-section">
                    <span className="ai-section-label">📖 {t('bookContext')}</span>
                    <p>{interpretation.context.bookContext}</p>
                  </div>
                )}
                {interpretation.context.biblicalContext && (
                  <div className="ai-context-section">
                    <span className="ai-section-label">📜 {t('biblicalContext')}</span>
                    <p>{interpretation.context.biblicalContext}</p>
                  </div>
                )}
              </>
            )}
            {!interpretation.context && (
              <p className="ai-no-content">{t('noData')}</p>
            )}
          </div>
        );

      case 'culture':
        return (
          <div className="ai-tab-content">
            {interpretation.cultural?.facts?.length > 0 && (
              <div className="ai-context-section">
                <span className="ai-section-label">🏺 {t('culture')}</span>
                <ul className="ai-facts">
                  {interpretation.cultural.facts.map((fact, idx) => (
                    <li key={idx} className="ai-fact">{fact}</li>
                  ))}
                </ul>
              </div>
            )}
            {interpretation.historical?.facts?.length > 0 && (
              <div className="ai-context-section">
                <span className="ai-section-label">📜 {t('historical')}</span>
                <ul className="ai-facts">
                  {interpretation.historical.facts.map((fact, idx) => (
                    <li key={idx} className="ai-fact">{fact}</li>
                  ))}
                </ul>
              </div>
            )}
            {(!interpretation.cultural?.facts?.length && !interpretation.historical?.facts?.length) && (
              <p className="ai-no-content">{t('noCultureHistory')}</p>
            )}
          </div>
        );

      case 'original':
        return (
          <div className="ai-tab-content">
            {interpretation.original && interpretation.original.keyWords?.length > 0 ? (
              <>
                <p className="ai-original-lang">{t('originalTab')}: {interpretation.original.language}</p>
                <div className="ai-keywords">
                  {interpretation.original.keyWords.map((word, idx) => (
                    <div key={idx} className="ai-keyword">
                      <div className="ai-keyword-original">{word.original}</div>
                      <div className="ai-keyword-translit">{word.transliteration}</div>
                      <div className="ai-keyword-meaning">{word.meaning}</div>
                    </div>
                  ))}
                </div>
                {interpretation.original.notes && (
                  <p className="ai-original-notes">{interpretation.original.notes}</p>
                )}
              </>
            ) : (
              <p className="ai-no-content">{t('noOriginal')}</p>
            )}
          </div>
        );

      case 'apply': {
        const app = interpretation?.application;
        const principle = app && typeof app === 'object' ? (app.principle ?? app.Principle ?? '') : '';
        const modernContext = app && typeof app === 'object'
          ? (app.modernContext ?? app.modern_context ?? app.ModernContext ?? '')
          : '';
        return (
          <div className="ai-tab-content">
            {app ? (
              <>
                {typeof app === 'string' ? (
                  <div className="ai-apply-block">
                    <p className="ai-apply-text">{app}</p>
                  </div>
                ) : (
                  <>
                    {principle && (
                      <div className="ai-context-section ai-apply-block">
                        <span className="ai-section-label">🧭 {t('principle')}</span>
                        <p className="ai-apply-text">{principle}</p>
                      </div>
                    )}
                    {modernContext && (
                      <div className="ai-context-section ai-apply-block">
                        <span className="ai-section-label">🌍 {t('today')}</span>
                        <p className="ai-apply-text">{modernContext}</p>
                      </div>
                    )}
                    {!principle && !modernContext && (
                      <p className="ai-no-content">{t('noApplicationYet')}</p>
                    )}
                  </>
                )}
              </>
            ) : (
              <p className="ai-no-content">{t('noData')}</p>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <>
      {toast && (
        <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 z-[9999] animate-slide-up">
          <div className="bg-green-500 text-white px-6 py-3 rounded-xl shadow-lg font-medium">
            {toast}
          </div>
        </div>
      )}
    <div className="ai-modal" onClick={onClose}>
      <div className="ai-content" onClick={(e) => e.stopPropagation()}>
        <div className="bible-modal-sheet-handle" aria-hidden="true" />
        {/* Фиксированная шапка */}
        <div className="ai-header-fixed">
          <div className="flex items-center gap-2 min-w-0">
            <span className="ai-icon flex-shrink-0" aria-hidden="true">✨</span>
            <h2 className="text-base font-semibold text-slate-700 truncate">{t('interpretation')}</h2>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={handleCopyLink}
              className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors"
              title={t('copyLink')}
              aria-label={t('copyLink')}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
          <button
            type="button"
            onClick={onClose}
              className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors text-lg font-light"
              aria-label={t('close')}
          >
            ×
          </button>
          </div>
        </div>
        
        {/* Скроллируемая область */}
        <div className="ai-scrollable-content">
          {loading && (
            <div className="ai-loading">
              <div className="ai-loading-spinner"></div>
              <p className="text-slate-500 mt-4">{t('analyzingScripture')}</p>
              <p className="text-slate-400 text-sm mt-2">{t('mayTakeMinutes')}</p>
            </div>
          )}
          
          {error && (
            <div className="ai-error">
              <p className="text-red-600 font-semibold">{t('interpretationLoadSoftErrorTitle')}</p>
              <p className="text-slate-500 mt-2">{error}</p>
              <button 
                onClick={loadInterpretation}
                className="ai-retry-button mt-4"
              >
                {t('retry')}
              </button>
            </div>
          )}
          
          {!loading && !error && interpretation && (
            <div className="ai-interpretation">
              {/* Референс и текст */}
              <div className="ai-verse-header">
                <div className="ai-verse-reference">{interpretation.reference}</div>
                <div className="ai-verse-text">«{interpretation.text}»</div>
              </div>
              
              {/* Горизонтальный слайдер табов */}
              <div className="ai-tabs-slider">
                <div 
                  className="ai-tabs-container"
                  ref={tabsContainerRef}
                >
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                      ref={(el) => {
                        if (el) tabButtonRefs.current[tab.id] = el;
                      }}
                      onClick={() => {
                        setActiveTab(tab.id);
                        // Центрируем кнопку при клике
                        const button = tabButtonRefs.current[tab.id];
                        const container = tabsContainerRef.current;
                        if (button && container) {
                          const buttonLeft = button.offsetLeft;
                          const buttonWidth = button.offsetWidth;
                          const containerWidth = container.offsetWidth;
                          const scrollLeft = buttonLeft - (containerWidth / 2) + (buttonWidth / 2);
                          container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
                        }
                      }}
                      className={`ai-tab-button ${activeTab === tab.id ? 'ai-tab-button-active' : ''}`}
                  >
                    <span className="ai-tab-icon">{tab.icon}</span>
                      <span className="ai-tab-label">{tab.label}</span>
                  </button>
                ))}
              </div>
              </div>
              
              {/* Контент таба (key чтобы при смене вкладки контент обновлялся) */}
              <div key={activeTab}>{renderTabContent()}</div>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}
